import {
  hostedAuthenticationRequired,
  hostedSession,
  loadMyPullInbox,
  type MyPullInbox,
  type OutstandingPullStatus,
  type PullStatusItem,
  type ReviewRequestItem,
} from "./hosted";
import { navigate } from "./navigation";
import {
  activateNotificationCenter,
  appendNotifications,
  clearNotificationCenter,
  markNotificationRead,
  type ChewNotification,
} from "./notification-center";
import { isHumanGitHubActor } from "../../shared/pull-attention";
import { publishSystemNotification, takeLaunchedNotification } from "./system-notifications";
import { activeVisibilityPollMs, startVisibilityPolling } from "./visibility-polling";

// Review notifications are discovered by any running CHEW window and published through its
// service worker so an installed PWA produces a persistent operating-system notification:
// while a tab is alive it polls the author's outstanding pull statuses (a
// webhook-fed D1 read, no GitHub rate limit) and announces transitions into a
// decisive review state. Deliberately not Web Push — no server state, at the
// cost of needing CHEW to be running somewhere.

const enabledStorageKey = "chew:review-notifications";
const snapshotStorageKey = "chew:review-notification-snapshot";
const leaseStorageKey = "chew:review-notification-lease";
const statusesEventName = "chew:pull-statuses";
export const reviewNotificationPollMs = activeVisibilityPollMs;

export type PullStatusSnapshot = Record<string, OutstandingPullStatus>;
export type ReviewRequestSnapshot = Record<string, string>;
export type PullAttentionSnapshot = Record<string, {
  ci: { state: "success" | "failure" | "pending" | "neutral" | "unavailable" | "unknown"; failed: number };
  latestComment: string;
}>;

// Bumped when the persisted shape changes. An unrecognised version reads as "no baseline", which
// reuses the existing first-poll behaviour instead of announcing everything at once on upgrade.
const snapshotVersion = 4;

interface PersistedSnapshot {
  v: number;
  login: string;
  statuses: PullStatusSnapshot;
  reviewRequests: ReviewRequestSnapshot;
  attention: PullAttentionSnapshot;
}

/**
 * A baseline belongs to one login. Without that check, the next person to sign in on a shared
 * profile inherits the previous one's snapshot, and every review already open against them looks
 * newly requested — a burst of false notifications on the very first poll.
 */
export function parseSnapshot(raw: string | null, login: string): PersistedSnapshot | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedSnapshot>;
    if (parsed.v !== snapshotVersion || parsed.login !== login || !parsed.statuses) return undefined;
    return {
      v: snapshotVersion,
      login,
      statuses: parsed.statuses,
      reviewRequests: parsed.reviewRequests ?? {},
      attention: parsed.attention ?? {},
    };
  } catch {
    return undefined;
  }
}

export function pullKey(pull: Pick<PullStatusItem, "repository" | "pullNumber">): string {
  return `${pull.repository}#${pull.pullNumber}`;
}

export function snapshotOf(pulls: PullStatusItem[]): PullStatusSnapshot {
  return Object.fromEntries(pulls.map((pull) => [pullKey(pull), pull.status]));
}

export function reviewRequestSnapshotOf(requests: ReviewRequestItem[]): ReviewRequestSnapshot {
  return Object.fromEntries(requests.map((request) => [pullKey(request), request.requestedAt]));
}

export function attentionSnapshotOf(pulls: PullStatusItem[]): PullAttentionSnapshot {
  return Object.fromEntries(pulls.map((pull) => [pullKey(pull), {
    ci: { state: pull.attention.ci?.state ?? "unknown", failed: pull.attention.ci?.failed ?? 0 },
    latestComment: pull.attention.conversation?.latest
      ? `${pull.attention.conversation.latest.createdAt}:${pull.attention.conversation.latest.author}:${pull.attention.conversation.latest.url}`
      : "",
  }]));
}

export function attentionNotifications(
  previous: PullAttentionSnapshot | undefined,
  pulls: PullStatusItem[],
  login: string,
  observedAt = new Date().toISOString(),
): ChewNotification[] {
  if (!previous) return [];
  const current = attentionSnapshotOf(pulls);
  return pulls.flatMap((pull) => {
    const key = pullKey(pull);
    const before = previous[key];
    if (!before) return [];
    const next = current[key]!;
    const notifications: ChewNotification[] = [];
    if (pull.attention.ci?.state === "failure" && (
      before.ci.state !== "failure" || before.ci.failed < next.ci.failed
    )) {
      const failed = pull.attention.ci.failed;
      notifications.push({
        id: `ci-failed:${key}:${observedAt}`,
        kind: "ci-failed",
        repository: pull.repository,
        pullNumber: pull.pullNumber,
        title: pull.title,
        detail: `${failed} ${failed === 1 ? "check is" : "checks are"} failing`,
        href: reviewPath(pull),
        occurredAt: observedAt,
      });
    }
    const latest = pull.attention.conversation?.latest;
    if (
      latest &&
      isHumanGitHubActor(latest.authorType) &&
      latest.author.toLocaleLowerCase() !== login.toLocaleLowerCase() &&
      before.latestComment !== next.latestComment
    ) {
      notifications.push({
        id: `comment-added:${key}:${latest.createdAt}:${latest.author}`,
        kind: "comment-added",
        repository: pull.repository,
        pullNumber: pull.pullNumber,
        title: pull.title,
        detail: `@${latest.author} commented on your PR`,
        href: latest.url || reviewPath(pull),
        occurredAt: latest.createdAt,
      });
    }
    return notifications;
  });
}

// A newer requestedAt means you were asked again — typically a re-request after a force-push — so
// it is worth announcing a second time.
export function reviewRequestTransitions(
  previous: ReviewRequestSnapshot | undefined,
  requests: ReviewRequestItem[],
): ReviewRequestItem[] {
  if (!previous) return [];
  return requests.filter((request) => {
    const before = previous[pullKey(request)];
    return !before || before < request.requestedAt;
  });
}

function shortRepository(repository: string): string {
  return repository.split("/")[1] ?? repository;
}

export function reviewRequestNotifications(
  previous: ReviewRequestSnapshot | undefined,
  requests: ReviewRequestItem[],
): ChewNotification[] {
  return reviewRequestTransitions(previous, requests).map((request) => ({
    id: `review-request:${pullKey(request)}:${request.requestedAt}`,
    kind: "review-requested",
    repository: request.repository,
    pullNumber: request.pullNumber,
    title: request.title,
    detail: `@${request.authorLogin} requested your review`,
    href: reviewPath(request),
    occurredAt: request.requestedAt,
  }));
}

// Only transitions from a previously seen different status count: the first
// poll establishes a baseline instead of announcing every already-reviewed PR.
export function reviewTransitions(
  previous: PullStatusSnapshot | undefined,
  pulls: PullStatusItem[],
  observedAt = new Date().toISOString(),
): ChewNotification[] {
  if (!previous) return [];
  return pulls.flatMap((pull) => {
    if (pull.status !== "approved" && pull.status !== "changes-requested") return [];
    const key = pullKey(pull);
    const before = previous[key];
    if (!before || before === pull.status) return [];
    const occurredAt = pull.activityAt ?? observedAt;
    const repository = shortRepository(pull.repository);
    return [{
      id: `pull-status:${key}:${pull.status}:${occurredAt}`,
      kind: pull.status,
      repository: pull.repository,
      pullNumber: pull.pullNumber,
      title: pull.title,
      detail: pull.status === "approved"
        ? `${repository}#${pull.pullNumber} was approved`
        : `${repository}#${pull.pullNumber} needs changes`,
      href: reviewPath(pull),
      occurredAt,
    }];
  });
}

export function notificationsEnabled(): boolean {
  return window.localStorage.getItem(enabledStorageKey) === "on";
}

export function setNotificationsEnabled(enabled: boolean): void {
  if (enabled) window.localStorage.setItem(enabledStorageKey, "on");
  else window.localStorage.removeItem(enabledStorageKey);
}

function readSnapshot(login: string): PersistedSnapshot | undefined {
  return parseSnapshot(window.localStorage.getItem(snapshotStorageKey), login);
}

// One tab polls at a time: a tab holds the lease by stamping a fresh expiry
// each round, and others stand down until the stamp goes stale. The snapshot
// lives in localStorage too, so leadership can move between tabs safely, and
// the notification tag deduplicates any handover race.
export function acquireLease(tabId: string, now: number): boolean {
  const raw = window.localStorage.getItem(leaseStorageKey);
  if (raw) {
    try {
      const lease = JSON.parse(raw) as { owner: string; expiresAt: number };
      if (lease.owner !== tabId && lease.expiresAt > now) return false;
    } catch {
      // Corrupt lease: claim it.
    }
  }
  window.localStorage.setItem(leaseStorageKey, JSON.stringify({ owner: tabId, expiresAt: now + reviewNotificationPollMs * 2 }));
  return true;
}

// Every tab needs an initialized local center, even though only one tab should poll the
// shared inbox. Otherwise followers return at the lease check with `ready: false` and leave
// the notification popover stuck on "Checking for activity" for as long as the leader lives.
export function prepareNotificationPoll(login: string, tabId: string, now = Date.now()): boolean {
  activateNotificationCenter(login);
  return acquireLease(tabId, now);
}

export function reviewPath(request: Pick<ReviewRequestItem, "repository" | "pullNumber">): string {
  return `/live/${request.repository}/${request.pullNumber}`;
}

async function pollOnce(tabId: string): Promise<void> {
  let login: string;
  try {
    // Memoised, so this costs nothing beyond the first call.
    login = (await hostedSession()).user.login;
  } catch (reason: unknown) {
    // A badge left over from a signed-out session is worse than no badge at all.
    if (hostedAuthenticationRequired(reason)) clearNotificationCenter();
    throw reason;
  }
  const ownsPollingLease = prepareNotificationPoll(login, tabId);
  const launchedNotification = takeLaunchedNotification();
  if (launchedNotification) markNotificationRead(launchedNotification);
  if (!ownsPollingLease) return;

  let inbox: MyPullInbox;
  try {
    inbox = await loadMyPullInbox();
  } catch (reason: unknown) {
    if (hostedAuthenticationRequired(reason)) clearNotificationCenter();
    throw reason;
  }
  const previous = readSnapshot(login);
  const incoming = [
    ...reviewTransitions(previous?.statuses, inbox.statuses),
    ...reviewRequestNotifications(previous?.reviewRequests, inbox.reviewRequests),
    ...attentionNotifications(previous?.attention, inbox.statuses, login),
  ];
  const snapshot = snapshotOf(inbox.statuses);
  window.localStorage.setItem(snapshotStorageKey, JSON.stringify({
    v: snapshotVersion,
    login,
    statuses: snapshot,
    reviewRequests: reviewRequestSnapshotOf(inbox.reviewRequests),
    attention: attentionSnapshotOf(inbox.statuses),
  } satisfies PersistedSnapshot));
  window.dispatchEvent(new CustomEvent(statusesEventName, { detail: snapshot }));
  const added = appendNotifications(login, incoming);

  if (!notificationsEnabled() || !("Notification" in window) || Notification.permission !== "granted") return;
  await Promise.all(added.map((item) => publishSystemNotification(item, () => {
      window.focus();
      markNotificationRead(item.id);
      if (item.href.startsWith("/")) navigate(item.href);
      else window.open(item.href, "_blank", "noopener");
    })));
}

let stopPolling: (() => void) | undefined;

export function startReviewNotificationPolling(): () => void {
  if (stopPolling) return () => undefined;
  const tabId = crypto.randomUUID();
  stopPolling = startVisibilityPolling(() => pollOnce(tabId).catch(() => undefined));
  return () => {
    stopPolling?.();
    stopPolling = undefined;
  };
}
